import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CartDrawer from '../../../components/feature/CartDrawer';

interface ShopProps {
  scrollY: number;
}

interface Product {
  id: string;
  title: string;
  description: string;
  price: string;
  compareAtPrice: string | null;
  image: string;
  category: string;
  tags: string[];
  availableForSale: boolean;
  variantId?: string;
}

export default function Shop({ scrollY }: ShopProps) {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('shop');
      if (element) {
        const rect = element.getBoundingClientRect();
        setIsVisible(rect.top < window.innerHeight * 0.7);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey) {
        console.error('Supabase configuration missing');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${supabaseUrl}/functions/v1/shopify-products`, {
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP error:', response.status, errorText);
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      
      if (data.error) {
        console.error('Shopify API error:', data.error);
        setLoading(false);
        return;
      }
      
      if (data.products && data.products.length > 0) {
        // Show 8 products on homepage to include new ones
        setProducts(data.products.slice(0, 8));
      } else {
        console.warn('No products returned from Shopify');
      }
    } catch (error) {
      console.error('Error fetching products:', error instanceof Error ? error.message : 'Unknown error', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!product.availableForSale) {
      alert('This product is currently unavailable');
      return;
    }

    setAddingToCart(product.id);

    try {
      // Get variant ID if not already available
      let variantId = product.variantId;
      
      if (!variantId) {
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/shopify-product-variant?productId=${encodeURIComponent(product.id)}`);
        const data = await response.json();
        variantId = data.variantId;
      }

      if (!variantId) {
        throw new Error('Product variant not found');
      }

      // Get existing cart
      const existingCart = localStorage.getItem('cart');
      const cart = existingCart ? JSON.parse(existingCart) : [];

      // Check if product already in cart
      const existingItemIndex = cart.findIndex((item: any) => item.variantId === variantId);

      if (existingItemIndex > -1) {
        cart[existingItemIndex].quantity += 1;
      } else {
        cart.push({
          variantId,
          productId: product.id,
          title: product.title,
          price: product.price,
          image: product.image,
          quantity: 1,
        });
      }

      localStorage.setItem('cart', JSON.stringify(cart));
      setIsCartOpen(true);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add product to cart. Please try again.');
    } finally {
      setAddingToCart(null);
    }
  };

  const handleProductClick = (productId: string) => {
    // Extract just the numeric ID from the Shopify GID
    const numericId = productId.split('/').pop();
    navigate(`/product/${numericId}`);
  };

  return (
    <>
      <section id="shop" className="py-20 bg-gradient-to-b from-amber-50 to-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-amber-800 to-red-700 bg-clip-text text-transparent">
              Shop Sacred Rituals
            </h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
              Discover our carefully curated collection of sacred wellness products, 
              each designed to elevate your daily rituals into transformative experiences.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-product-shop>
              {products.map((product, index) => (
                <div
                  key={product.id}
                  className={`group bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-700 hover:scale-105 hover:shadow-2xl cursor-pointer ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                  onMouseEnter={() => setHoveredProduct(product.id)}
                  onMouseLeave={() => setHoveredProduct(null)}
                  onClick={() => handleProductClick(product.id)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.title}
                      className={`w-full h-64 object-cover object-top transition-all duration-700 group-hover:scale-110 ${
                        hoveredProduct === product.id ? 'opacity-90' : 'opacity-100'
                      }`}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent transition-all duration-500 ${
                      hoveredProduct === product.id ? 'opacity-100' : 'opacity-0'
                    }`}>
                      <div className={`absolute bottom-4 left-4 right-4 transition-all duration-500 ${
                        hoveredProduct === product.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                      }`}>
                        <button 
                          onClick={(e) => addToCart(product, e)}
                          disabled={!product.availableForSale || addingToCart === product.id}
                          className="w-full bg-gradient-to-r from-amber-700 via-red-800 to-red-900 text-white py-3 px-4 rounded-full font-semibold hover:from-amber-800 hover:via-red-900 hover:to-red-950 transition-all duration-300 whitespace-nowrap shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        >
                          {addingToCart === product.id ? (
                            <span className="flex items-center justify-center gap-2">
                              <i className="ri-loader-4-line animate-spin"></i>
                              Adding...
                            </span>
                          ) : !product.availableForSale ? (
                            'Out of Stock'
                          ) : (
                            'Add to Cart'
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="absolute top-4 left-4">
                      <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                        {product.category}
                      </span>
                    </div>
                    {product.compareAtPrice && parseFloat(product.compareAtPrice) > parseFloat(product.price) && (
                      <div className="absolute top-4 right-4">
                        <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                          Sale
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-stone-800 mb-2 group-hover:text-amber-700 transition-colors duration-300">
                      {product.title}
                    </h3>
                    <p className="text-stone-600 mb-4 leading-relaxed line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-amber-700">${product.price}</span>
                        {product.compareAtPrice && parseFloat(product.compareAtPrice) > parseFloat(product.price) && (
                          <span className="text-lg text-gray-400 line-through">${product.compareAtPrice}</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-star-fill text-amber-400 text-sm"></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={`text-center mt-12 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <button 
              onClick={() => navigate('/products')}
              className="bg-gradient-to-r from-amber-700 via-red-800 to-red-900 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-amber-800 hover:via-red-900 hover:to-red-950 transform hover:scale-105 transition-all duration-300 shadow-xl cursor-pointer whitespace-nowrap"
            >
              View All Products
            </button>
          </div>
        </div>
      </section>

      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
}
